import { useGetOrganizationQuery } from '@codezo/graphql';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetPageMeta } from '../../../hooks/useSetPageMeta';
import { BrandDetailV2 } from '../BrandDetailV2';

/**
 * A brand kit as its own page, under the client it belongs to.
 *
 * ── Why this route exists ──
 *
 * `BrandDetailV2` was only ever reachable from inside the v1 client page's
 * Brand kit tab, where the open brand lived in component state: a reload
 * dropped you back on the list, and there was no URL to send anyone. Giving it
 * a route fixes both by removing the thing that caused them — there is no state
 * to restore, and the address bar already says which brand you are on.
 *
 * It sits under `/clients/:id/` rather than at a top level because a brand
 * belongs to exactly one client, and the breadcrumb has to be able to say so.
 */
export function BrandDetailPageV2() {
	const { id, brandId } = useParams<{ id: string; brandId: string }>();
	const navigate = useNavigate();

	/* Only for the breadcrumb and the back link — the brand's own data is the
	   detail component's business. */
	const { data } = useGetOrganizationQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const org = data?.getOrganization;

	useSetPageMeta(org?.name ?? 'Brand', [
		{ label: 'Clients', path: '/clients' },
		{ label: org?.name ?? 'Client', path: `/clients/${id}` },
		{ label: 'Brand' },
	]);

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<BrandDetailV2
				brandId={brandId!}
				/* Back goes to the client page, not to history: arriving here from
				   a shared link has nothing to go back to. */
				onBack={() => navigate(`/clients/${id}`)}
			/>
		</div>
	);
}
